<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { community } from '../../data/projects'

const route = useRoute()
const mobileMenuOpen = ref(false)
const mobileToggle = ref<HTMLButtonElement | null>(null)
const mobileNavigation = ref<HTMLElement | null>(null)

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/events', label: 'Events' },
  { path: '/leaderboards', label: 'Leaderboards' },
  { path: '/dashboard', label: 'Dashboard' },
]

function isActive(path: string): boolean {
  return path === '/' ? route.path === path : route.path.startsWith(path)
}

async function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
  if (mobileMenuOpen.value) {
    await nextTick()
    mobileNavigation.value?.querySelector<HTMLElement>('a')?.focus()
  }
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
  nextTick(() => mobileToggle.value?.focus())
}

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
})
</script>

<template>
  <header class="site-header">
    <div class="header-inner">
      <RouterLink to="/" class="brand" aria-label="The Calming Storm home">
        <span class="brand-mark" aria-hidden="true">
          <span class="mark-core" />
        </span>
        <span class="brand-copy">
          <span class="brand-name">The Calming Storm</span>
          <span class="brand-kicker">Minecraft community</span>
        </span>
      </RouterLink>

      <nav class="desktop-nav" aria-label="Primary navigation">
        <RouterLink
          v-for="link in navLinks"
          :key="link.path"
          :to="link.path"
          :class="['nav-link', { active: isActive(link.path) }]"
          :aria-current="isActive(link.path) ? 'page' : undefined"
        >
          {{ link.label }}
        </RouterLink>
      </nav>

      <div class="header-actions">
        <a class="discord-link" :href="community.discord" target="_blank" rel="noopener noreferrer">
          Join Discord
          <span aria-hidden="true">↗</span>
        </a>
        <button
          ref="mobileToggle"
          class="mobile-toggle"
          type="button"
          :aria-expanded="mobileMenuOpen"
          aria-controls="mobile-navigation"
          :aria-label="mobileMenuOpen ? 'Close navigation' : 'Open navigation'"
          @click="toggleMobileMenu"
        >
          <span :class="['hamburger', { open: mobileMenuOpen }]" />
        </button>
      </div>
    </div>

    <Transition name="menu">
      <nav v-if="mobileMenuOpen" id="mobile-navigation" ref="mobileNavigation" class="mobile-nav" aria-label="Mobile navigation" @keydown.esc.stop="closeMobileMenu">
        <RouterLink
          v-for="link in navLinks"
          :key="link.path"
          :to="link.path"
          :class="['mobile-link', { active: isActive(link.path) }]"
        >
          <span>{{ link.label }}</span>
          <span aria-hidden="true">→</span>
        </RouterLink>
        <div class="mobile-community">
          <a :href="community.discord" target="_blank" rel="noopener noreferrer">Join Discord</a>
          <a :href="community.donate" target="_blank" rel="noopener noreferrer">Support the servers</a>
        </div>
      </nav>
    </Transition>
  </header>
</template>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--border-subtle);
  background: rgba(8, 10, 18, 0.82);
  backdrop-filter: blur(22px) saturate(130%);
  -webkit-backdrop-filter: blur(22px) saturate(130%);
}

.site-header::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 1px;
  content: '';
  background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.42), rgba(103, 232, 249, 0.25), transparent);
  opacity: 0.55;
}

.header-inner {
  width: min(100%, var(--site-width));
  height: 74px;
  margin-inline: auto;
  padding-inline: var(--gutter);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1.25rem;
}

.brand {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
}

.brand:hover {
  color: var(--text-primary);
}

.brand-mark {
  position: relative;
  width: 34px;
  height: 34px;
  overflow: hidden;
  border: 1px solid rgba(196, 181, 253, 0.42);
  border-radius: 9px;
  background:
    linear-gradient(135deg, rgba(167, 139, 250, 0.24), rgba(103, 232, 249, 0.06)),
    var(--bg-secondary);
  box-shadow: inset 0 0 18px rgba(139, 92, 246, 0.18), 0 0 24px rgba(139, 92, 246, 0.12);
  transform: rotate(45deg);
}

.mark-core {
  position: absolute;
  inset: 9px;
  border: 2px solid var(--color-secondary);
  border-top-color: var(--color-primary-soft);
  opacity: 0.9;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.brand-name {
  font-size: 0.98rem;
  font-weight: 760;
  letter-spacing: -0.025em;
}

.brand-kicker {
  margin-top: 0.23rem;
  color: var(--text-muted);
  font-size: 0.62rem;
  font-weight: 650;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.desktop-nav {
  justify-self: center;
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.3rem;
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.018);
}

.nav-link {
  position: relative;
  padding: 0.48rem 0.83rem;
  border-radius: 999px;
  color: var(--text-secondary);
  font-size: 0.82rem;
  font-weight: 590;
}

.nav-link:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.035);
}

.nav-link.active {
  color: var(--text-primary);
  background: var(--color-primary-dim);
  box-shadow: inset 0 0 0 1px rgba(167, 139, 250, 0.12);
}

.nav-link.active::after {
  position: absolute;
  right: 35%;
  bottom: 2px;
  left: 35%;
  height: 1px;
  content: '';
  background: var(--color-secondary);
  box-shadow: 0 0 8px var(--color-secondary);
}

.header-actions {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.discord-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.58rem 0.85rem;
  border: 1px solid rgba(167, 139, 250, 0.32);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.22), rgba(139, 92, 246, 0.08));
  font-size: 0.78rem;
  font-weight: 650;
}

.discord-link:hover {
  border-color: rgba(103, 232, 249, 0.42);
  color: #fff;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.32), rgba(8, 145, 178, 0.12));
  transform: translateY(-1px);
}

.mobile-toggle {
  display: none;
  width: 42px;
  height: 42px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  background: var(--bg-card);
  cursor: pointer;
}

.hamburger,
.hamburger::before,
.hamburger::after {
  position: absolute;
  width: 18px;
  height: 1.5px;
  border-radius: 2px;
  background: currentColor;
  transition: transform var(--transition-normal), opacity var(--transition-fast);
}

.hamburger {
  position: relative;
  display: block;
  margin: auto;
}

.hamburger::before,
.hamburger::after {
  left: 0;
  content: '';
}

.hamburger::before { transform: translateY(-6px); }
.hamburger::after { transform: translateY(6px); }
.hamburger.open { background: transparent; }
.hamburger.open::before { transform: rotate(45deg); }
.hamburger.open::after { transform: rotate(-45deg); }

.mobile-nav {
  display: none;
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 160ms ease, transform 220ms var(--ease-out);
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 940px) {
  .header-inner {
    grid-template-columns: 1fr auto;
  }

  .desktop-nav,
  .discord-link {
    display: none;
  }

  .mobile-toggle {
    display: grid;
    place-items: center;
  }

  .mobile-nav {
    width: min(100%, var(--site-width));
    margin-inline: auto;
    padding: 0.55rem var(--gutter) 1rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.45rem;
  }

  .mobile-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    padding: 0.7rem 0.85rem;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.018);
    font-size: 0.86rem;
    font-weight: 590;
  }

  .mobile-link.active {
    border-color: rgba(167, 139, 250, 0.3);
    color: var(--text-primary);
    background: var(--color-primary-dim);
  }

  .mobile-community {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.65rem 0.2rem 0;
  }

  .mobile-community a {
    color: var(--text-muted);
    font-size: 0.75rem;
  }
}

@media (max-width: 520px) {
  .header-inner {
    height: 68px;
  }

  .brand-mark {
    width: 30px;
    height: 30px;
  }

  .brand-name {
    font-size: 0.9rem;
  }

  .brand-kicker {
    font-size: 0.55rem;
  }

  .mobile-nav {
    grid-template-columns: 1fr;
  }

  .mobile-community {
    grid-column: auto;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>
